import { useEffect, useMemo, useState } from "react";
import { Search } from "lucide-react";
import { api } from "@/lib/api";
import { PageHero } from "@/pages/Agence";

const ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

function firstLetter(term) {
  const c = (term || "").trim().toUpperCase().charAt(0);
  return /[A-Z]/.test(c) ? c : "#";
}

export default function Lexique() {
  const [entries, setEntries] = useState([]);
  const [search, setSearch] = useState("");

  useEffect(() => {
    api
      .get("/lexique")
      .then((r) => setEntries(r.data))
      .catch(() => setEntries([]));
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return entries;
    return entries.filter(
      (e) =>
        e.term.toLowerCase().includes(q) ||
        e.definition.toLowerCase().includes(q)
    );
  }, [entries, search]);

  const grouped = useMemo(() => {
    const map = {};
    for (const e of filtered) {
      const k = firstLetter(e.term);
      (map[k] = map[k] || []).push(e);
    }
    return map;
  }, [filtered]);

  const presentLetters = new Set(Object.keys(grouped));

  return (
    <div data-testid="page-lexique">
      <PageHero
        overline="Lexique"
        title="Le vocabulaire de la copropriété, expliqué simplement."
        subtitle="Définitions des termes essentiels pour mieux comprendre vos droits et obligations."
      />

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-12">
            <aside className="lg:col-span-3">
              <div className="lg:sticky lg:top-[160px]">
                <div className="relative mb-8">
                  <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#64748B]" />
                  <input
                    data-testid="lexique-search"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Rechercher un terme..."
                    className="w-full pl-11 pr-4 py-3 border border-[#E2E8F0] bg-white text-sm focus:border-[#0F172A] outline-none transition-colors"
                  />
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {ALPHABET.map((l) => {
                    const active = presentLetters.has(l);
                    return (
                      <a
                        key={l}
                        href={active ? `#letter-${l}` : undefined}
                        data-testid={`alpha-${l}`}
                        className={`w-9 h-9 flex items-center justify-center text-sm font-medium border transition-all ${
                          active
                            ? "border-[#0F172A] text-[#0F172A] hover:bg-[#0F172A] hover:text-white"
                            : "border-[#E2E8F0] text-[#CBD5E1] cursor-default"
                        }`}
                      >
                        {l}
                      </a>
                    );
                  })}
                </div>
              </div>
            </aside>

            <div className="lg:col-span-9">
              {Object.keys(grouped).length === 0 ? (
                <p className="text-[#64748B]">Aucun résultat.</p>
              ) : (
                Object.keys(grouped)
                  .sort()
                  .map((letter) => (
                    <section key={letter} id={`letter-${letter}`} className="mb-14 scroll-mt-28">
                      <div className="flex items-center gap-4 mb-6">
                        <span className="font-serif text-5xl text-[#B8935A] leading-none">
                          {letter}
                        </span>
                        <div className="flex-1 h-px bg-[#E2E8F0]" />
                      </div>
                      <dl className="space-y-6">
                        {grouped[letter].map((e) => (
                          <div
                            key={e.id}
                            data-testid={`lexique-entry-${e.id}`}
                            className="grid sm:grid-cols-12 gap-3 sm:gap-6"
                          >
                            <dt className="sm:col-span-4 font-serif text-lg text-[#0F172A]">
                              {e.term}
                            </dt>
                            <dd
                              className="sm:col-span-8 text-[#475569] leading-relaxed"
                              dangerouslySetInnerHTML={{ __html: e.definition }}
                            />
                          </div>
                        ))}
                      </dl>
                    </section>
                  ))
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
